import React from 'react';
import {
	Alert,
	Box,
	Button,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

type Props = {
	driveAccessToken: string;
	driveRefreshToken: string;
	driveClientId: string;
	driveClientSecret: string;
	setDriveAccessToken: (value: string) => void;
	setDriveRefreshToken: (value: string) => void;
	setDriveClientId: (value: string) => void;
	setDriveClientSecret: (value: string) => void;
	driveSetupInfo: {
		has_access_token?: boolean;
		has_refresh_token?: boolean;
		has_client_id?: boolean;
		has_client_secret?: boolean;
		access_token_expires_at?: number;
	} | null;
	onSaveDriveSetup: () => void;
	onDriveHealthCheck: () => void;
	healthReport: any | null;
	busy: boolean;
	secretariesParentFolderUrl: string;
	juryParentFolderUrl: string;
};

const StatusIcon = ({ ok }: { ok: boolean }) =>
	ok ? (
		<CheckCircleIcon
			fontSize='small'
			color='success'
		/>
	) : (
		<ErrorIcon
			fontSize='small'
			color='error'
		/>
	);

const Stage3DriveSetupTab = ({
	driveAccessToken,
	driveRefreshToken,
	driveClientId,
	driveClientSecret,
	setDriveAccessToken,
	setDriveRefreshToken,
	setDriveClientId,
	setDriveClientSecret,
	driveSetupInfo,
	onSaveDriveSetup,
	onDriveHealthCheck,
	healthReport,
	busy,
	secretariesParentFolderUrl,
	juryParentFolderUrl,
}: Props) => {
	const secretaryChecks: any[] = Array.isArray(healthReport?.secretaries) ? healthReport.secretaries : [];
	const juryChecks: any[] = Array.isArray(healthReport?.jury_pairs) ? healthReport.jury_pairs : [];

	return (
		<>
			<Typography variant='subtitle1'>Google Drive - setup</Typography>
			<Typography variant='caption'>Token OAuth i podstawowa konfiguracja dostępu API Google Drive.</Typography>
			<TextField
				fullWidth
				size='small'
				label='Access token (opcjonalnie, jednorazowy override)'
				value={driveAccessToken}
				onChange={(e) => setDriveAccessToken(e.target.value)}
				sx={{ mt: 1 }}
			/>
			<TextField
				fullWidth
				size='small'
				label='Refresh token (zalecane)'
				value={driveRefreshToken}
				onChange={(e) => setDriveRefreshToken(e.target.value)}
				sx={{ mt: 1 }}
			/>
			<TextField
				fullWidth
				size='small'
				label='OAuth Client ID'
				value={driveClientId}
				onChange={(e) => setDriveClientId(e.target.value)}
				sx={{ mt: 1 }}
			/>
			<TextField
				fullWidth
				size='small'
				type='password'
				label='OAuth Client Secret'
				value={driveClientSecret}
				onChange={(e) => setDriveClientSecret(e.target.value)}
				sx={{ mt: 1 }}
			/>
			<Typography
				variant='body2'
				sx={{ mt: 1 }}
			>
				Zapisany access token: {driveSetupInfo?.has_access_token ? 'TAK' : 'NIE'} | refresh token:{' '}
				{driveSetupInfo?.has_refresh_token ? 'TAK' : 'NIE'} | client_id: {driveSetupInfo?.has_client_id ? 'TAK' : 'NIE'}{' '}
				| client_secret: {driveSetupInfo?.has_client_secret ? 'TAK' : 'NIE'}
			</Typography>
			<Typography variant='caption'>
				Ważność access token (unix): {driveSetupInfo?.access_token_expires_at || 0}
			</Typography>
			<Alert
				severity='info'
				sx={{ mt: 1 }}
			>
				Po zapisaniu refresh tokenu + client_id + client_secret system automatycznie odświeża access token przed
				operacjami Drive.
			</Alert>

			{(secretariesParentFolderUrl || juryParentFolderUrl) && (
				<Box sx={{ mt: 1 }}>
					<Typography variant='subtitle2'>Foldery nadrzędne</Typography>
					<TableContainer component={Paper}>
						<Table size='small'>
							<TableBody>
								{secretariesParentFolderUrl && (
									<TableRow>
										<TableCell sx={{ fontWeight: 500 }}>Sekretarze</TableCell>
										<TableCell>
											<a
												href={secretariesParentFolderUrl}
												target='_blank'
												rel='noopener noreferrer'
											>
												{secretariesParentFolderUrl}
											</a>
										</TableCell>
									</TableRow>
								)}
								{juryParentFolderUrl && (
									<TableRow>
										<TableCell sx={{ fontWeight: 500 }}>Komisje</TableCell>
										<TableCell>
											<a
												href={juryParentFolderUrl}
												target='_blank'
												rel='noopener noreferrer'
											>
												{juryParentFolderUrl}
											</a>
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</TableContainer>
				</Box>
			)}

			<Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
				<Button
					variant='contained'
					onClick={onSaveDriveSetup}
					disabled={busy}
				>
					Zapisz setup Drive
				</Button>
				<Button
					variant='outlined'
					onClick={onDriveHealthCheck}
					disabled={busy}
				>
					Health-check dostępu
				</Button>
			</Box>

			{healthReport && (
				<Box sx={{ mt: 2 }}>
					<Typography variant='subtitle2'>Wynik health-check</Typography>
					<TableContainer component={Paper}>
						<Table size='small'>
							<TableBody>
								<TableRow>
									<TableCell sx={{ fontWeight: 500 }}>Token API</TableCell>
									<TableCell>
										<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
											<StatusIcon ok={Boolean(healthReport.token_ok)} />
											{healthReport.token_ok ? 'OK' : healthReport.token_error || 'Błąd'}
										</Box>
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					</TableContainer>

					{secretaryChecks.length > 0 && (
						<Box sx={{ mt: 1 }}>
							<Typography variant='subtitle2'>Foldery sekretarzy</Typography>
							<TableContainer
								component={Paper}
								sx={{ maxHeight: 220 }}
							>
								<Table
									size='small'
									stickyHeader
								>
									<TableHead>
										<TableRow>
											<TableCell>Okręg</TableCell>
											<TableCell>Status</TableCell>
											<TableCell>Błąd</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{secretaryChecks.map((row: any) => (
											<TableRow key={row.district_id}>
												<TableCell>{row.district_name}</TableCell>
												<TableCell>
													<StatusIcon ok={Boolean(row.ok)} />
												</TableCell>
												<TableCell>{row.error || '-'}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</TableContainer>
						</Box>
					)}

					{juryChecks.length > 0 && (
						<Box sx={{ mt: 1 }}>
							<Typography variant='subtitle2'>Foldery komisji</Typography>
							<TableContainer
								component={Paper}
								sx={{ maxHeight: 220 }}
							>
								<Table
									size='small'
									stickyHeader
								>
									<TableHead>
										<TableRow>
											<TableCell>Komisja</TableCell>
											<TableCell>Status</TableCell>
											<TableCell>Błąd</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{juryChecks.map((row: any) => (
											<TableRow key={row.jury_pair_id}>
												<TableCell>{row.jury_pair_name}</TableCell>
												<TableCell>
													<StatusIcon ok={Boolean(row.ok)} />
												</TableCell>
												<TableCell>{row.error || '-'}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</TableContainer>
						</Box>
					)}
				</Box>
			)}
		</>
	);
};

export default Stage3DriveSetupTab;
